import { useState, useEffect } from "react"
import { ChevronUp, ChevronDown, ArrowRight } from "lucide-react"

type CardVariant = "text" | "stat" | "image-right" | "image-large"

interface TimelineCard {
  title?: string
  desc: string
  stat?: string
  statLabel?: string
  image?: string
  link?: string
  linkLabel?: string
  variant: CardVariant
}

interface TimelineYear {
  year: string
  cards: TimelineCard[]
}

const timeline: TimelineYear[] = [
  {
    year: "2025",
    cards: [
      {
        desc: "RSA Tech Group enters the Cognitive MSP Era, operating as a modern Managed Services and Digital Engineering partner.",
        link: "/about",
        linkLabel: "LEARN MORE",
        variant: "text",
      },
      {
        stat: "5+",
        statLabel: "AI powered products launched including LeoRix AI.",
        variant: "stat",
      },
      {
        desc: "Showcasing healthcare AI innovation at HIMSS 2025 and consumer tech breakthroughs at CES 2026.",
        image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=200&q=80",
        variant: "image-right",
      },
      {
        desc: "Expanding into new industry verticals with dedicated solutions for healthcare, insurance, and retail.",
        image: "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=600&q=80",
        variant: "image-large",
      },
      {
        desc: "Enterprise AI transformation services becomes a core practice, serving Fortune 500 clients.",
        variant: "text",
      },
      {
        stat: "99.9%",
        statLabel: "Platform uptime achieved across all managed service clients.",
        variant: "stat",
      },
    ],
  },
  {
    year: "2023",
    cards: [
      {
        desc: "AI driven automation embedded into managed services workflows including monitoring, analytics, and quality assurance.",
        link: "/what-we-do",
        linkLabel: "LEARN MORE",
        variant: "text",
      },
      {
        stat: "60%",
        statLabel: "Reduction in incident response times through predictive analytics and ML models.",
        variant: "stat",
      },
      {
        desc: "Built end to end AIOps pipelines enabling proactive system health monitoring and autonomous remediation.",
        image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=200&q=80",
        variant: "image-right",
      },
      {
        desc: "Intelligent automation frameworks deployed across client environments at enterprise scale.",
        image: "https://images.unsplash.com/photo-1555255707-c07966088b7b?auto=format&fit=crop&w=600&q=80",
        variant: "image-large",
      },
      {
        desc: "Expanded data engineering practice with Snowflake and Databricks integrations.",
        variant: "text",
      },
      {
        stat: "40+",
        statLabel: "Enterprise clients actively using AI enabled managed services.",
        variant: "stat",
      },
    ],
  },
  {
    year: "2021",
    cards: [
      {
        desc: "Strengthened digital engineering capabilities across enterprise CMS platforms, cloud native apps, and DevOps automation.",
        link: "/what-we-do",
        linkLabel: "LEARN MORE",
        variant: "text",
      },
      {
        stat: "3x",
        statLabel: "Faster delivery cycles achieved through standardized CI/CD pipelines.",
        variant: "stat",
      },
      {
        desc: "Managing complex multi platform ecosystems at enterprise scale with Salesforce, Sitecore, and Snowflake.",
        image: "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=200&q=80",
        variant: "image-right",
      },
      {
        desc: "DevOps at scale with infrastructure as code practices across all client engagements.",
        image: "https://images.unsplash.com/photo-1618401471353-b98afee0b2eb?auto=format&fit=crop&w=600&q=80",
        variant: "image-large",
      },
      {
        desc: "Launched dedicated Salesforce services and implementation practice.",
        variant: "text",
      },
      {
        stat: "25+",
        statLabel: "Enterprise platforms under active management and continuous delivery.",
        variant: "stat",
      },
    ],
  },
  {
    year: "2018",
    cards: [
      {
        desc: "Pivoted toward cloud first initiatives, modernizing legacy platforms and migrating systems to AWS and Azure.",
        link: "/what-we-do/cloud-engineering",
        linkLabel: "LEARN MORE",
        variant: "text",
      },
      {
        stat: "50+",
        statLabel: "Enterprise applications migrated to cloud native architectures.",
        variant: "stat",
      },
      {
        desc: "Established SOC2 compliant processes and enterprise grade security frameworks across all offerings.",
        image: "https://images.unsplash.com/photo-1563986768609-322da13575f2?auto=format&fit=crop&w=200&q=80",
        variant: "image-right",
      },
      {
        desc: "Cloud transformation partnerships with AWS and Microsoft Azure accelerating enterprise modernization.",
        image: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&w=600&q=80",
        variant: "image-large",
      },
      {
        desc: "Built scalable, secure, and resilient digital architectures for regulated industries.",
        variant: "text",
      },
      {
        stat: "100%",
        statLabel: "Cloud migrations completed on time with zero data loss.",
        variant: "stat",
      },
    ],
  },
  {
    year: "2015",
    cards: [
      {
        desc: "RSA Tech Group expanded into Managed Services, taking full ownership of application platforms and infrastructure operations.",
        link: "/what-we-do",
        linkLabel: "LEARN MORE",
        variant: "text",
      },
      {
        stat: "24/7",
        statLabel: "Follow the sun delivery model launched with global teams across time zones.",
        variant: "stat",
      },
      {
        desc: "Formed key technology alliances with Salesforce, Microsoft, and AWS for integrated enterprise solutions.",
        image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=200&q=80",
        variant: "image-right",
      },
      {
        desc: "Global team collaboration ensuring round the clock platform reliability and support.",
        image: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=600&q=80",
        variant: "image-large",
      },
      {
        desc: "Shifted toward outcome driven delivery models with measurable SLAs and KPIs.",
        variant: "text",
      },
      {
        stat: "15+",
        statLabel: "Long term enterprise partnerships established across multiple industries.",
        variant: "stat",
      },
    ],
  },
  {
    year: "2012",
    cards: [
      {
        desc: "RSA Tech Group was founded as a technology driven services firm focused on building reliable enterprise systems.",
        link: "/about",
        linkLabel: "LEARN MORE",
        variant: "text",
      },
      {
        stat: "1",
        statLabel: "A single office, a bold vision, and a commitment to engineering excellence from day one.",
        variant: "stat",
      },
      {
        desc: "Secured foundational enterprise contracts in healthcare, financial services, and retail.",
        image: "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=200&q=80",
        variant: "image-right",
      },
      {
        desc: "Building the foundation with a focus on reliability, security, and long term client partnerships.",
        image: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=600&q=80",
        variant: "image-large",
      },
      {
        desc: "Established core engineering principles as the foundation of every engagement.",
        variant: "text",
      },
      {
        stat: "100%",
        statLabel: "Client retention rate in the first three years of operation.",
        variant: "stat",
      },
    ],
  },
]

export const JourneyTimeline = () => {
  const [activeYearIndex, setActiveYearIndex] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [slideDirection, setSlideDirection] = useState<"up" | "down">("down")
  const [page, setPage] = useState(0)

  const activeYear = timeline[activeYearIndex]
  const cardsPerPage = 6
  const totalPages = Math.ceil(activeYear.cards.length / cardsPerPage)
  const visibleCards = activeYear.cards.slice(
    page * cardsPerPage,
    page * cardsPerPage + cardsPerPage
  )

  const switchYear = (index: number) => {
    if (index === activeYearIndex || index < 0 || index >= timeline.length) return
    setSlideDirection(index > activeYearIndex ? "down" : "up")
    setIsTransitioning(true)
    setTimeout(() => {
      setActiveYearIndex(index)
      setPage(0)
      setTimeout(() => setIsTransitioning(false), 50)
    }, 200)
  }

  useEffect(() => {
    setPage(0)
  }, [activeYearIndex])

  return (
    <section className="py-24 relative overflow-hidden bg-[#f4f7fb]">
      <div className="absolute inset-0 z-0">
        <img
          src="/about_timeline_bg.png"
          alt=""
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-[#f4f7fb]/80" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <h2 className="text-4xl md:text-5xl font-bold mb-5 text-slate-900 tracking-tight">
            Our Journey of <span className="text-blue-600">Evolution</span>
          </h2>
          <p className="text-slate-500 max-w-2xl mx-auto text-lg leading-relaxed font-light">
            From building reliable foundations to engineering the cognitive enterprise.
            A decade of continuous innovation and operational excellence.
          </p>
        </div>

        {/* Year heading above cards */}
        <div
          className={`mb-6 transition-all duration-200 ${
            isTransitioning
              ? `opacity-0 ${slideDirection === "down" ? "-translate-y-2" : "translate-y-2"}`
              : "opacity-100 translate-y-0"
          }`}
        >
          <h3 className="text-4xl md:text-5xl font-light text-blue-600 tracking-tight pl-20 md:pl-24 lg:pl-28">
            {activeYear.year}
          </h3>
        </div>

        {/* Body: years + grid */}
        <div className="flex gap-0 items-start">
          {/* LEFT: Year nav */}
          <div className="shrink-0 w-16 md:w-20 lg:w-24 flex flex-col items-center">
            <button
              onClick={() => switchYear(activeYearIndex - 1)}
              disabled={activeYearIndex === 0}
              className="p-1 text-slate-400 hover:text-blue-600 transition-colors disabled:opacity-20 disabled:cursor-not-allowed mb-1"
              aria-label="Previous year"
            >
              <ChevronUp className="w-5 h-5" />
            </button>

            <div className="relative flex flex-col items-start pl-1">
              <div className="absolute left-[5px] top-3 bottom-3 w-px bg-slate-200/80 z-0" />

              {timeline.map((item, index) => (
                <button
                  key={item.year}
                  onClick={() => switchYear(index)}
                  className="relative z-10 flex items-center gap-3 py-[9px] group"
                >
                  <div
                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 shrink-0 ${
                      activeYearIndex === index
                        ? "bg-red-500 scale-110"
                        : "bg-transparent border border-slate-300 group-hover:border-slate-500"
                    }`}
                  />
                  <span
                    className={`text-sm transition-all duration-300 whitespace-nowrap ${
                      activeYearIndex === index
                        ? "text-red-500 font-bold"
                        : "text-slate-500 group-hover:text-slate-700 font-medium"
                    }`}
                  >
                    {item.year}
                  </span>
                </button>
              ))}
            </div>

            <button
              onClick={() => switchYear(activeYearIndex + 1)}
              disabled={activeYearIndex === timeline.length - 1}
              className="p-1 text-slate-400 hover:text-blue-600 transition-colors disabled:opacity-20 disabled:cursor-not-allowed mt-1"
              aria-label="Next year"
            >
              <ChevronDown className="w-5 h-5" />
            </button>
          </div>

          {/* RIGHT: 3x2 Card Grid */}
          <div className="flex-1 min-w-0">
            <div
              className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 transition-all duration-200 ${
                isTransitioning
                  ? `opacity-0 ${slideDirection === "down" ? "translate-y-5" : "-translate-y-5"}`
                  : "opacity-100 translate-y-0"
              }`}
            >
              {visibleCards.map((card, i) => {
                if (card.variant === "stat") {
                  return (
                    <div
                      key={`${activeYear.year}-${i}`}
                      className="bg-[#eaf1fb] rounded-xl border border-[#dce6f2] p-6 flex flex-col items-center justify-center text-center min-h-[190px] hover:shadow-md transition-shadow"
                    >
                      <span className="text-5xl md:text-6xl font-light text-blue-400 leading-none mb-3">
                        {card.stat}
                      </span>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        {card.statLabel}
                      </p>
                    </div>
                  )
                }

                if (card.variant === "image-right") {
                  return (
                    <div
                      key={`${activeYear.year}-${i}`}
                      className="bg-[#eaf1fb] rounded-xl border border-[#dce6f2] p-6 flex items-start gap-4 min-h-[190px] hover:shadow-md transition-shadow"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-700 text-sm leading-relaxed">
                          {card.desc}
                        </p>
                      </div>
                      {card.image && (
                        <img
                          src={card.image}
                          alt=""
                          className="w-20 h-20 rounded-lg object-cover shrink-0"
                        />
                      )}
                    </div>
                  )
                }

                if (card.variant === "image-large") {
                  return (
                    <div
                      key={`${activeYear.year}-${i}`}
                      className="bg-[#eaf1fb] rounded-xl border border-[#dce6f2] overflow-hidden min-h-[190px] hover:shadow-md transition-shadow flex flex-col"
                    >
                      {card.image && (
                        <img
                          src={card.image}
                          alt=""
                          className="w-full h-28 object-cover"
                        />
                      )}
                      <div className="p-5">
                        <p className="text-slate-700 text-sm leading-relaxed">
                          {card.desc}
                        </p>
                      </div>
                    </div>
                  )
                }

                // Default: text variant
                return (
                  <div
                    key={`${activeYear.year}-${i}`}
                    className="bg-[#eaf1fb] rounded-xl border border-[#dce6f2] p-6 flex flex-col justify-between min-h-[190px] hover:shadow-md transition-shadow"
                  >
                    <p className="text-slate-700 text-sm leading-relaxed">
                      {card.desc}
                    </p>
                    {card.link && card.linkLabel && (
                      <a
                        href={card.link}
                        className="inline-flex items-center gap-1.5 text-blue-600 text-xs font-bold uppercase tracking-wider mt-4 hover:text-blue-700 transition-colors"
                      >
                        {card.linkLabel}
                        <ArrowRight className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Pagination dots */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2.5 mt-8">
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i)}
                    className={`rounded-full transition-all duration-300 ${
                      page === i
                        ? "w-7 h-2 bg-slate-500"
                        : "w-2 h-2 bg-slate-300 hover:bg-slate-400"
                    }`}
                    aria-label={`Page ${i + 1}`}
                  />
                ))}
              </div>
            )}

            {/* Single page dot indicator */}
            {totalPages <= 1 && (
              <div className="flex items-center justify-center gap-2.5 mt-8">
                <div className="w-7 h-2 rounded-full bg-slate-400" />
                <div className="w-2 h-2 rounded-full bg-slate-300" />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
